import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Layers, IdCard, CreditCard, ArrowRight, Zap } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_cards: 0, total_templates: 0, credits: 0, free_used: false });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/stats");
        setStats(s.data);
        const r = await api.get("/idcards");
        setRecent(r.data.slice(0, 5));
      } catch (e) { console.error("Dashboard load failed:", e); }
    })();
  }, []);

  const statCards = [
    { label: "Total ID Cards", value: stats.total_cards, icon: IdCard },
    { label: "Templates", value: stats.total_templates, icon: Layers },
    { label: "Credits", value: stats.credits, icon: CreditCard },
    { label: "Free Card", value: stats.free_used ? "USED" : "AVAILABLE", icon: Zap },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">// Dashboard</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Hello, {user?.name?.split(" ")[0] || "friend"}.</h1>
          <p className="text-gray-600 mt-2">Forge ID cards in minutes.</p>
        </div>
        <Link to="/templates/new" className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-bold uppercase tracking-wider px-6 py-4 brutal-border brutal-shadow brutal-hover text-sm" data-testid="new-template-btn">
          <Plus className="w-4 h-4"/> New Template
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <div key={s.label} className="bg-white brutal-border brutal-shadow p-5" data-testid={`stat-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{s.label}</span>
              <s.icon className="w-4 h-4 text-black" strokeWidth={2}/>
            </div>
            <div className="font-display font-black text-3xl">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Link to="/templates" className="bg-white brutal-border brutal-shadow p-6 brutal-hover" data-testid="quick-templates">
          <Layers className="w-8 h-8 mb-4 text-[#002FA7]" strokeWidth={2}/>
          <div className="font-display font-bold text-2xl mb-1">Templates</div>
          <div className="text-sm text-gray-600">Upload & manage card designs</div>
          <div className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-widest font-bold">Open <ArrowRight className="w-3 h-3"/></div>
        </Link>
        <Link to="/library" className="bg-white brutal-border brutal-shadow p-6 brutal-hover" data-testid="quick-library">
          <IdCard className="w-8 h-8 mb-4 text-[#002FA7]" strokeWidth={2}/>
          <div className="font-display font-bold text-2xl mb-1">Library</div>
          <div className="text-sm text-gray-600">All generated ID cards</div>
          <div className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-widest font-bold">Open <ArrowRight className="w-3 h-3"/></div>
        </Link>
        <Link to="/billing" className="bg-black text-white brutal-border brutal-shadow p-6 brutal-hover" data-testid="quick-billing">
          <CreditCard className="w-8 h-8 mb-4 text-[#22C55E]" strokeWidth={2}/>
          <div className="font-display font-bold text-2xl mb-1">Buy Credits</div>
          <div className="text-sm text-gray-300">₹10 per ID card</div>
          <div className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-widest font-bold">Open <ArrowRight className="w-3 h-3"/></div>
        </Link>
      </div>

      <div className="bg-white brutal-border brutal-shadow">
        <div className="p-5 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-gray-500">// Recent</div>
            <h2 className="font-display font-bold text-2xl">Recent ID Cards</h2>
          </div>
          <Link to="/library" className="font-mono text-xs uppercase tracking-widest font-bold hover:underline">View all →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-gray-500 font-mono text-sm uppercase tracking-widest">// No cards yet. Create a template to start.</div>
        ) : (
          <div className="divide-y-2 divide-black">
            {recent.map(c => (
              <div key={c.id} className="p-4 flex items-center justify-between" data-testid={`recent-${c.id}`}>
                <div>
                  <div className="font-bold">{c.person_name}</div>
                  <div className="font-mono text-xs text-gray-500">{c.template_name} · {new Date(c.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
